/* eslint-disable import/no-duplicates */
import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import { format, isBefore, parseISO, startOfToday } from 'date-fns';

import { Container, Button, Row, Col } from 'react-bootstrap';

import api from '../../../../services/api';

import { formatPrice, TIPO_USUARIO } from '../../../../utils/funcoes';

import { useToast } from '../../../../hooks/toast';
import { useAuth } from '../../../../hooks/auth';

import Input from '../../../../components/Input';
import SelectSync, { IOption } from '../../../../components/SelectSync';
import Loading from '../../../../components/Loading';

import getValidationErrors from '../../../../utils/getValidationErrors';

import { RelatorioHeader } from './styles';

import getResponseErrors from '../../../../utils/getResponseErrors';
import HeaderSimples from '../../components/HeaderSimples';

interface IRelatorio {
  CobCodigo: number;
  CobData: Date;
  CobDataFormatada: string;
  CobPlaCodigo: number;
  CobObservacao: string;
  CobLink: string;
  CprValor: number;
  CprValorFormatado: string;
  CprPago: boolean;
  CprPagoFormatado: string;
  CprValorPag: number;
  CprValorPagFormatado: string;
  CprDataVenc: Date;
  CprDataVencFormatado: string;
  CprHistorico: string;
  CprDataPag: Date;
  CprDataPagFormatado: string;
  PacNome: string;
  vencido?: boolean;
}

interface IFormBusca {
  dataInicio: Date;
  dataFim: Date;
  status?: string;
}

interface IFormPaginacao extends IFormBusca {
  Pago?: boolean;
}

export interface IPaginacao {
  termoBusca: IFormPaginacao;
  limite: number;
  pagina: number;
}

interface IDataExibe {
  dataHora: string;
  dataDiaSemana: string;
}

const CobrancaAcordos: React.FC = () => {
  const history = useHistory();
  const { user } = useAuth();

  useEffect(() => {
    if (user.UsuTipo >= TIPO_USUARIO.ATENDENTE_CAIXA) {
      history.push('/sempermissao');
    }
  }, [history, user.UsuTipo]);

  const { addToast } = useToast();

  const formRefBusca = useRef<FormHandles>(null);
  const [loading, setLoading] = useState(false);
  const [dadosRelatorio, setDadosRelatorio] = useState<IRelatorio[]>();
  const [paginacao, setPaginacao] = useState<IPaginacao | undefined>(undefined);
  const [totalRegistros, setTotalRegistros] = useState<number | undefined>(
    undefined,
  );
  const [totalRecebido, setTotalRecebido] = useState<number>(0);

  const initialData = {
    dataInicio: format(new Date(), 'yyyy-MM-dd'),
    dataFim: format(new Date(), 'yyyy-MM-dd'),
  };
  const [dataExibe, setDataExibe] = useState<IDataExibe>();

  useEffect(() => {
    async function loadCobrancas(): Promise<void> {
      if (paginacao) {
        setLoading(true);
        try {
          const dataInicioSelecionada = parseISO(
            paginacao?.termoBusca.dataInicio.toString(),
          );
          const dataFimSelecionada = parseISO(
            paginacao?.termoBusca.dataFim.toString(),
          );

          const dataInicioFormatada = format(
            dataInicioSelecionada,
            'yyyy-MM-dd',
          );
          const dataFimFormatada = format(dataFimSelecionada, 'yyyy-MM-dd');

          setDataExibe({
            dataDiaSemana: `${format(
              dataInicioSelecionada,
              'dd/MM/yyyy',
            )} até ${format(dataFimSelecionada, 'dd/MM/yyyy')}`,
            dataHora: format(new Date(), 'dd/MM/yyyy HH:mm'),
          });

          const parametrosOpcionais =
            paginacao?.termoBusca.Pago !== undefined
              ? `&Pago=${paginacao?.termoBusca.Pago}`
              : '';

          const response = await api.get<{
            count: number;
            acordos: IRelatorio[];
          }>(
            `/cobranca/acordos/?pagina=${paginacao.pagina}&limite=${paginacao.limite}&DataIni=${dataInicioFormatada}&DataFim=${dataFimFormatada}${parametrosOpcionais}`,
          );

          setTotalRegistros(
            response.data.count === 0 ? undefined : response.data.count,
          );
          setTotalRecebido(
            response.data.acordos.reduce((tot, ac) => {
              return tot + Number(ac.CprValorPag);
            }, 0),
          );

          const dadosFormatados = response.data.acordos.map(rel => {
            return {
              ...rel,
              CobDataFormatada: format(
                parseISO(rel.CobData.toString()),
                'dd/MM/yyyy',
              ),
              CprDataVencFormatado: format(
                parseISO(rel.CprDataVenc.toString()),
                'dd/MM/yyyy',
              ),
              CprDataPagFormatada: rel.CprDataPag
                ? format(parseISO(rel.CprDataPag.toString()), 'dd/MM/yyyy')
                : '',
              CprPagoFormatado: rel.CprPago ? 'Sim' : 'Não',
              CprValorFormatado: formatPrice(rel.CprValor),
              CprValorPagFormatado: formatPrice(rel.CprValorPag),
              vencido:
                !rel.CprPago &&
                isBefore(parseISO(rel.CprDataVenc.toString()), startOfToday()),
            };
          });

          setDadosRelatorio(dadosFormatados);
        } catch (error) {
          const mensagem = getResponseErrors(error);
          addToast({
            type: 'error',
            title: 'Não foi possível carregar dados de cobrança',
            description: mensagem,
          });
        } finally {
          setLoading(false);
        }
      }
    }
    loadCobrancas();
  }, [paginacao, addToast]);

  const handleSubmitBusca = useCallback(async (dados: IFormBusca) => {
    try {
      formRefBusca.current?.setErrors({});
      const schema = Yup.object().shape({
        dataInicio: Yup.date().required(),
        dataFim: Yup.date().required(),
        status: Yup.string().nullable(true),
      });

      await schema.validate(dados, {
        abortEarly: false,
      });

      setPaginacao({
        termoBusca: {
          dataInicio: dados.dataInicio,
          dataFim: dados.dataFim,
          Pago:
            dados?.status !== undefined && dados.status !== ''
              ? dados.status === 'Pago'
              : undefined,
        },
        pagina: 1,
        limite: 50,
      });
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const errors = getValidationErrors(error);
        formRefBusca.current?.setErrors(errors);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // const handleDownload = useCallback(async () => {
  //   setLoading(true);
  //   try {
  //     const dados: IFormBusca = {
  //       dataInicio: formRefBusca.current?.getFieldValue('dataInicio'),
  //       dataFim: formRefBusca.current?.getFieldValue('dataFim'),
  //       paciente: formRefBusca.current?.getFieldValue('paciente'),
  //       UsuCodigo: formRefBusca.current?.getFieldValue('UsuCodigo'),
  //     };
  //     console.log(dados);
  //     formRefBusca.current?.setErrors({});
  //     const schema = Yup.object().shape({
  //       dataInicio: Yup.date().required(),
  //       dataFim: Yup.date().required(),
  //       UsuCodigo: Yup.string().nullable(true),
  //     });

  //     await schema.validate(dados, {
  //       abortEarly: false,
  //     });

  //     const dataInicioSelecionada = parseISO(dados.dataInicio.toString());
  //     const dataFimSelecionada = parseISO(dados.dataFim.toString());

  //     const dataInicioFormatada = format(dataInicioSelecionada, 'yyyy-MM-dd');
  //     const dataFimFormatada = format(dataFimSelecionada, 'yyyy-MM-dd');

  //     let parametrosOpcionais = dados.UsuCodigo
  //       ? `&UsuCodigo=${dados.UsuCodigo}`
  //       : '';

  //     parametrosOpcionais =
  //       dados.paciente !== '' ? `&paciente=${dados.paciente}` : '';

  //     const response = await api.get(
  //       `/cobranca/exportar?pagina=${1}&limite=${1}&DataIni=${dataInicioFormatada}&DataFim=${dataFimFormatada}${parametrosOpcionais}`,
  //     );

  //     const link = document.createElement('a');
  //     link.href = response.data.url;
  //     link.setAttribute(
  //       'download',
  //       `cobrancasRealizadas${Math.floor(Date.now() / 1000)}.xlsx`,
  //     );
  //     document.body.appendChild(link);
  //     link.click();
  //     link.remove();
  //   } catch (error) {
  //     if (error instanceof Yup.ValidationError) {
  //       const errors = getValidationErrors(error);
  //       formRefBusca.current?.setErrors(errors);
  //     } else {
  //       const mensagem = getResponseErrors(error);
  //       addToast({
  //         type: 'error',
  //         title: 'Não foi possível carregar dados',
  //         description: mensagem,
  //       });
  //     }
  //   } finally {
  //     setLoading(false);
  //   }
  // }, [addToast]);

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const handleOnChange = useCallback(() => {}, []);

  // const handleProximaPagina = useCallback((page: number) => {
  //   setPaginacao(state => {
  //     return state && { ...state, pagina: page };
  //   });
  // }, []);

  return (
    <Container fluid className="mt-2">
      {loading && <Loading />}
      <>
        <div className="d-print-none bg-white border p-2 mb-2">
          <div className="p-2 mb-2">
            <h2 id="topoBusca">Acordos Realizados com Boleto</h2>
          </div>

          <Form
            ref={formRefBusca}
            initialData={initialData}
            onSubmit={handleSubmitBusca}
          >
            <Row>
              <Col md={2}>
                <Input name="dataInicio" label="De" type="date" required />
              </Col>
              <Col md={2}>
                <Input name="dataFim" label="Até" type="date" required />
              </Col>

              <Col md={3}>
                <SelectSync
                  name="status"
                  label="Status"
                  options={[
                    { label: 'Todos', value: '' },
                    { label: 'Pago', value: 'Pago' },
                    { label: 'Não Pago', value: 'Não Pago' },
                  ]}
                  isClearable
                  handleSelectChange={handleOnChange}
                />
              </Col>

              <Col md={1}>
                <br />
                <Button type="submit" size="sm" variant="outline-secondary">
                  BUSCAR
                </Button>
              </Col>
              <Col md={1}>
                {/* <br />
                <Button
                  type="button"
                  size="sm"
                  variant="outline-success"
                  disabled={!dadosRelatorio || dadosRelatorio.length === 0}
                  onClick={() => handleDownload()}
                >
                  <FaFileDownload />
                </Button> */}
              </Col>
            </Row>
          </Form>
        </div>

        <div className="bg-white border p-2 my-3">
          <HeaderSimples dataImpressao={dataExibe?.dataHora} />
        </div>

        <RelatorioHeader>
          ACORDO COBRANÇAS {dataExibe?.dataDiaSemana}
        </RelatorioHeader>
        <div className="bg-white border p-2">
          {dadosRelatorio && dadosRelatorio.length > 0 && (
            <>
              <table
                className="table table-hover table-sm table-font-small table-bordered"
                style={{ borderWidth: '0px' }}
              >
                <thead>
                  <tr className="text-center">
                    <th style={{ width: '90px' }}>Data Cob.</th>
                    <th style={{ width: '300px' }}>Paciente</th>
                    <th style={{ width: '180px' }}>Histórico</th>
                    <th>Valor</th>
                    <th>Pago</th>
                    <th>Valor Pago</th>
                    <th style={{ width: '90px' }}>Data Venc.</th>
                  </tr>
                </thead>
                <tbody>
                  {dadosRelatorio.map(rel => (
                    <tr
                      key={`cobranca-${rel.CobCodigo}`}
                      style={{
                        fontSize: '.7rem',
                        height: '30px',
                        color: rel.vencido ? 'red' : '#000',
                      }}
                    >
                      <td>{rel.CobDataFormatada}</td>
                      <td>{rel.PacNome}</td>
                      <td>{rel.CprHistorico}</td>
                      <td>{rel.CprValorFormatado}</td>
                      <td>{rel.CprPagoFormatado}</td>
                      <td>{rel.CprValorPagFormatado}</td>
                      <td>{rel.CprDataVencFormatado}</td>
                    </tr>
                  ))}
                  <tr>
                    <td colSpan={5}>Total:</td>
                    <td colSpan={2}>{formatPrice(totalRecebido)}</td>
                  </tr>
                </tbody>
              </table>
              {/* <Paginacao
                limitePagina={paginacao?.limite || 30}
                paginasVizinhas={2}
                totalRegistros={totalRegistros}
                paginaInicio={paginacao?.pagina}
                handleProximaPagina={handleProximaPagina}
              /> */}
            </>
          )}
          {(!dadosRelatorio || dadosRelatorio.length === 0) && (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: '250px' }}
            >
              <h5>Nenhum registro encontrado</h5>
            </div>
          )}
        </div>
      </>
    </Container>
  );
};

export default CobrancaAcordos;
